import DiffMatchPatch from 'diff-match-patch';
import { useCallback } from 'react';
import { create } from 'zustand';

import { DATA_CONVERTERS } from '@/components/Code/Code.utils';
import { DiffChangeType } from '@/types/diff';
import { sortKeysDeep } from '@/utils/sortKeysDeep';

import {
  ExampleSchemaWithRunsAndOptionalFields,
  RunOutputSchema,
} from '../../../types/schema';
import {
  ExperimentLanguage,
  TExperimentDiffs,
  TExperimentDiffsByLanguage,
} from '../types';

export type TGetRunDiffArgs = {
  exampleId: string;
  runId: string;
  runOutputs: RunOutputSchema | undefined;
  runOutputsPreview: string | undefined;
  exampleOutputs: ExampleSchemaWithRunsAndOptionalFields['outputs'] | undefined;
};

interface DiffsStore {
  storedDiffs: TExperimentDiffsByLanguage;
  setStoredDiffs: (
    diffs:
      | TExperimentDiffsByLanguage
      | ((prev: TExperimentDiffsByLanguage) => TExperimentDiffsByLanguage)
  ) => void;
}

export const useExperimentDiffsStore = create<DiffsStore>((set) => ({
  storedDiffs: {
    pretty: {},
    json: {},
    yaml: {},
  },
  setStoredDiffs: (diffs) =>
    set((state) => ({
      storedDiffs:
        typeof diffs === 'function' ? diffs(state.storedDiffs) : diffs,
    })),
}));

export const useGetExperimentDiffs = (
  language: ExperimentLanguage
): {
  getRunDiffs: (args: TGetRunDiffArgs) => void;
  storedDiffs: TExperimentDiffs;
} => {
  const { storedDiffs, setStoredDiffs } = useExperimentDiffsStore();
  const getRunDiffs: (args: TGetRunDiffArgs) => void = useCallback(
    ({
      exampleId,
      runId,
      runOutputs,
      runOutputsPreview,
      exampleOutputs,
    }: TGetRunDiffArgs) => {
      if ((!runOutputs && !runOutputsPreview) || language === 'pretty') return;
      // Check if we need to recalculate. If we already have diffs for this run and the output is the same, no need to recalculate but
      // update local state to match diffs for this run/example.
      let needsRecalculation = true;
      setStoredDiffs((prev) => {
        const existingDiffs = prev[language][exampleId];
        const existingRunDiffs = existingDiffs?.runs[runId];
        const existingRunDiffOutput = existingRunDiffs?.runOutput;
        if (existingRunDiffs && existingRunDiffOutput === runOutputs) {
          needsRecalculation = false;
          return {
            ...prev,
            [language]: {
              ...prev[language],
              [exampleId]: {
                exampleOutput: existingRunDiffs.exampleOutput,
                runs: existingDiffs.runs,
              },
            },
          };
        }
        return prev;
      });
      if (!needsRecalculation) return;

      const dmp = new DiffMatchPatch();

      const converter = DATA_CONVERTERS[language];

      const outputString = runOutputs
        ? converter.stringify(sortKeysDeep(runOutputs))
        : runOutputsPreview;
      const exampleOutputString = converter.stringify(
        sortKeysDeep(exampleOutputs)
      );

      if (!outputString || !exampleOutputString) return undefined;
      const diffs = dmp.diff_main(exampleOutputString, outputString);
      dmp.diff_cleanupSemantic(diffs);

      const mappedDiffs = diffs.reduce(
        (acc, d) => {
          const [changeType] = d;
          if (
            changeType === DiffMatchPatch.DIFF_INSERT ||
            changeType === DiffMatchPatch.DIFF_EQUAL
          ) {
            acc.runOutput.push(d);
          }
          if (
            changeType === DiffMatchPatch.DIFF_DELETE ||
            changeType === DiffMatchPatch.DIFF_EQUAL
          ) {
            acc.exampleOutput.push(d);
          }
          return acc;
        },
        {
          exampleOutput: [] as DiffChangeType[],
          runOutput: [] as DiffChangeType[],
        }
      );
      setStoredDiffs((prev) => ({
        ...prev,
        [language]: {
          ...prev[language],
          [exampleId]: {
            exampleOutput: mappedDiffs.exampleOutput,
            runs: {
              ...prev[language][exampleId]?.runs,
              [runId]: {
                exampleOutput: mappedDiffs.exampleOutput,
                runOutput: mappedDiffs.runOutput,
              },
            },
          },
        },
      }));
      return;
    },
    [language, setStoredDiffs]
  );

  return {
    getRunDiffs,
    storedDiffs: storedDiffs[language],
  };
};
